import { createElement, type ReactElement } from "react";
import type Configuration from "../types/Configuration";
import { MilestonesSectionConfigurations } from "../types/MilestonesSectionConfigurations";
import {
  DEFAULT_SECTION_ORDER,
  type SectionKey,
  type SectionOrderEntry,
} from "../types/SectionOrder";
import ClinicalInsightsSection from "./sections/clinical-insights-section";
import ConsiderationSection from "./sections/consideration-section";
import InsightsSection from "./sections/insights-section";
import MilestonesSection from "./sections/milestones-section";
import NotesSection from "./sections/notes-section";

/**
 * Map of orderable section keys to a rendered ReactElement (or null when the
 * section is disabled by its feature flag). PatientInfo and DischargePlan are
 * NOT in here — they're fixed headers rendered above the orderable region.
 */
export type SectionRegistry = Record<SectionKey, ReactElement | null>;

/** Build the registry for the current Configuration. */
export function buildSectionRegistry(
  configuration: Partial<Configuration>
): SectionRegistry {
  const isMilestonesEnabled =
    configuration.milestones_section !==
    MilestonesSectionConfigurations.DISABLED;

  return {
    milestones: isMilestonesEnabled
      ? createElement(MilestonesSection, {
          key: "milestones",
          configuration,
        })
      : null,
    notes: configuration.notes_section_enabled
      ? createElement(NotesSection, { key: "notes" })
      : null,
    insights: configuration.insights_section_enabled
      ? createElement(InsightsSection, { key: "insights" })
      : null,
    consideration: configuration.patient_consideration_enabled
      ? createElement(ConsiderationSection, {
          key: "consideration",
          configuration,
        })
      : null,
    clinical_insights: configuration.clinical_insights_display_enabled
      ? createElement(ClinicalInsightsSection, {
          key: "clinical_insights",
          configuration,
        })
      : null,
  };
}

export interface LayoutGroups {
  /** Flat list in declared order, with disabled sections filtered out. */
  narrowSections: ReactElement[];
  /** Wide layout left-column sections in declared order. */
  leftColumn: ReactElement[];
  /** Wide layout right-column sections in declared order. */
  rightColumn: ReactElement[];
}

/**
 * Split an ordered section list into the three render slots. Disabled
 * sections (whose registry entry is null) are dropped uniformly.
 */
export function splitForLayout(
  ordered: SectionOrderEntry[] | null | undefined,
  registry: SectionRegistry
): LayoutGroups {
  const entries =
    ordered && ordered.length > 0 ? ordered : DEFAULT_SECTION_ORDER;

  const narrowSections: ReactElement[] = [];
  const leftColumn: ReactElement[] = [];
  const rightColumn: ReactElement[] = [];

  for (const entry of entries) {
    const node = registry[entry.key];
    if (!node) continue;
    narrowSections.push(node);
    if (entry.column === "left") {
      leftColumn.push(node);
    } else if (entry.column === "right") {
      rightColumn.push(node);
    }
  }

  return { narrowSections, leftColumn, rightColumn };
}
